import { useState } from "react";
import * as XLSX from "xlsx";
import { ViewPdfButton } from "../components/view-pdf-button";
import { InvoiceViewerOverlay } from "@/components/billing/InvoiceViewerOverlay";
import type { InvoiceViewerInvoice } from "@/components/billing/InvoiceViewerOverlay";
import { CreditNoteDialog } from "@/components/billing/CreditNoteDialog";
import type { CreditNoteResult } from "@/components/billing/CreditNoteDialog";
import { WriteOffDialog } from "@/components/billing/WriteOffDialog";
import type { WriteOffItem, WriteOffResult } from "@/components/billing/WriteOffDialog";
import { WipFilterSheet } from "@/components/billing/WipFilterSheet";
import { DEFAULT_WIP_FILTERS, countActiveWipFilters } from "@/components/billing/wip-filter-types";
import type { WipFilters } from "@/components/billing/wip-filter-types";
import { AgedWipStrip } from "@/components/billing/AgedWipStrip";
import type { AgeBand, WipItemForAging } from "@/components/billing/AgedWipStrip";
import { SendInvoiceStep } from "@/components/billing/SendInvoiceStep";
import { ResendInvoiceDialog } from "@/components/billing/ResendInvoiceDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { Input } from "@/components/ui/input";

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";



import {
  FileText,
  Settings2,
  SlidersHorizontal,
  Trash2,
  Receipt,
  Search,
  X,
  RefreshCw,
  MoreHorizontal,
  Eye,
  Send,
  Download,
  CreditCard,
  Ban,
  Filter,
  User,
} from "lucide-react";
import { SensitiveData } from "@/components/sensitive-data";

import { FixedFeeBillingTab } from "@/components/billing/FixedFeeBillingTab";

import {
  DataTableEnhanced,
  columnDefinitionsToVisibility,
  type ColumnDef,
} from '@/components/ui/data-table-enhanced';
import { PaginationControls } from '@/components/pagination-controls';
import { calculatePagination } from '@/types/pagination';

import { mapInvoiceStatus, deriveInvoiceDisplayStatus, writeOffWipItems as writeOffWipItemsApi, undoWriteOff as undoWriteOffApi, fetchBillingInvoices, createCreditNote, updateDraftEmailData } from "@/lib/data-service";
import { computeInvoicedTotal, splitGrossIntoNetAndVat } from "@/lib/billing-summary";
import { DEMO_INVOICES } from "@/lib/demo-invoices";
import { getClientPhone } from "@/lib/client-contact-utils";
import { sumWipQuantity } from "@/lib/wip-filtering";
import type { BillingInvoice } from "@/lib/data-service";
import type { Milestone } from "@/types";
import type { ColumnDefinition } from "@/components/column-configurator-drawer";

import { formatCurrency } from "@/lib/utils";
import { getStoredInvoicePdf, openPdfInNewTab, downloadPdf } from "@/services/invoice-pdf-service";

import { useProjectDetailsContext } from "../context";
import { useCompanyDetails } from "@/hooks/use-company-details";

// Total Invoiced / Outstanding / Paid are hidden until they reconcile with
// Access Financials — set to true to re-enable.
const SHOW_INVOICE_SUMMARY_TOTALS: boolean = false;

export function BillingTab() {
  const ctx = useProjectDetailsContext();
  // Destructure all needed values from context
  const {
    project, client,
    enrichedBudgetData,
    milestones,
    billingInvoices, billingLoading, billingActionLoading, setBillingActionLoading,
    pdfExistsSet, wipSummary,
    fixedFeeInvoices,
    handleDeleteDraft,
    refetchBillingData,
    wipTotalItems,
    wipItems, filteredAndSortedWipItems, groupedWipItems, paginatedWipItems,
    selectedWipItems, setSelectedWipItems, selectedWipTotals,
    wipFilterOptions,
    wipSearchQuery, setWipSearchQuery, wipTypeFilter, setWipTypeFilter,
    wipResourceFilters, setWipResourceFilters, wipGroupBy, setWipGroupBy,
    wipDateFrom, setWipDateFrom, wipDateTo, setWipDateTo,
    wipCurrentPage, setWipCurrentPage, wipItemsPerPage, setWipItemsPerPage,
    wipColumnSorts, setWipColumnSorts,
    handleSelectWipItem, handleSelectAllWipItems, handleWipPageChange, handleWipPageSizeChange, handleRefreshWip,
    invoiceCurrentPage, setInvoiceCurrentPage, invoiceItemsPerPage, setInvoiceItemsPerPage,
    invoiceTotalCount,
    invoiceTotalInvoiced, invoiceTotalOutstanding, invoiceTotalPaid,
    invoicedNetTotal,
    invoiceColumnSorts, setInvoiceColumnSorts, invoiceStatusFilter, setInvoiceStatusFilter,
    invoiceSearchQuery, setInvoiceSearchQuery,
    invoiceColumns, visibleInvoiceColumns,
    setInvoiceColumnConfigOpen,
    wipColumns, visibleWipColumns, setWipColumnConfigOpen,
    setInvoiceWizardOpen,
    handleEditDraftInvoice,
    setFixedFeeMilestone,
    setCreateDraftDialogOpen,
    projectSettings,
    setBillingSettingsDrawerOpen,
    effectiveCompletionPercent,
    formatProjectType,
    dataMode,
    addInvoiceSendAuditEvent,
    billingSubTab, setBillingSubTab,
    // Additional items that may be used
  } = ctx;

  const { details: companyDetails } = useCompanyDetails();

  if (!project) return null;

  // ── Feature 2: Invoice Viewer Overlay state ────────────────────────────────
  // Tracks which invoice (if any) is currently being viewed in the full-screen
  // PDF overlay. null = overlay closed.
  // TODO (Engineering — Feature 2): When the overlay is opened, also kick off
  //   a fetch for real preview data: GET /billing/invoices/:id/preview
  const [viewerInvoice, setViewerInvoice] = useState<InvoiceViewerInvoice | null>(null);
  const [sendDialogInvoice, setSendDialogInvoice] = useState<{ invoiceNumber: string; invoiceId?: number; clientEmail?: string; initialEmailDraft?: { to?: string; cc?: string; bcc?: string; subject?: string; body?: string; templateId?: string } } | null>(null);

  // ── Feature 7: Resend Invoice state ────────────────────────────────────────
  // resendTargetInvoice — the invoice currently being resent (null = dialog closed).
  //
  // TODO (Engineering — Feature 7): Remove resendTargetInvoice once the dialog is
  //   wired to POST /api/invoices/{id}/resend. The dialog is currently fully
  //   client-side with mock send history stored in component state only.
  const [resendTargetInvoice, setResendTargetInvoice] = useState<{
    id: number;
    invoiceNumber: string | null;
    amount: number;
    due: string | null;
  } | null>(null);

  // ── Feature 3: Credit Note dialog state ────────────────────────────────────
  const [creditNoteTargetInvoice, setCreditNoteTargetInvoice] = useState<{
    id: number;
    invoiceNumber: string | null;
    amount: number;
    status: string;
  } | null>(null);

  // ── Feature 4: Write-Off state ──────────────────────────────────────────────
  const [writeOffDialogItems, setWriteOffDialogItems] = useState<WriteOffItem[]>([]);
  const [activeAgeBand, setActiveAgeBand] = useState<AgeBand | null>(null);
  // ── WIP Filter Sheet ─────────────────────────────────────────────────────
  const [wipFilterSheetOpen, setWipFilterSheetOpen] = useState(false);
  const [wipFilters, setWipFilters] = useState<WipFilters>(DEFAULT_WIP_FILTERS);

  /**
   * Called when the user confirms write-off in the dialog.
   * Calls the backend API to write off WIP items, then re-fetches billing data.
   */
  async function handleWriteOffConfirm(result: WriteOffResult) {
    const numericIds = result.itemIds
      .map((id) => parseInt(id.replace("wip-", ""), 10))
      .filter((id) => !isNaN(id));
    if (numericIds.length === 0) return;
    try {
      setBillingActionLoading(true);
      await writeOffWipItemsApi(numericIds, result.reasonCode ?? undefined);
      setWriteOffDialogItems([]);
      setSelectedWipItems([]);
      const count = result.itemIds.length;
      toast.success(`${count} item${count > 1 ? "s" : ""} written off`, {
        description: result.reasonLabel
          ? `Reason: ${result.reasonLabel}`
          : "No reason provided.",
      });
      await refetchBillingData();
    } catch (error) {
      console.error("Failed to write off WIP items:", error);
      toast.error("Failed to write off items. Please try again.");
    } finally {
      setBillingActionLoading(false);
    }
  }

  // ── Apply WIP filters from the filter sheet ──────────────────────────────
  // Syncs the unified WipFilters object back into the individual hook setters
  // that drive the server/computed filter logic.
  function handleApplyWipFilters(f: WipFilters) {
    setWipFilters(f);
    // Sync into hook-level setters so filteredAndSortedWipItems stays correct
    setWipTypeFilter(f.type);
    setWipGroupBy(f.groupBy);
    setWipDateFrom(f.dateFrom);
    setWipDateTo(f.dateTo);
    setWipResourceFilters(f.resources);
    // Reset to page 1 when filters change so pagination reflects filtered count
    setWipCurrentPage(1);
  }

  async function handleUndoWriteOff(itemId: string) {
    const numericId = parseInt(itemId.replace("wip-", ""), 10);
    if (isNaN(numericId)) return;
    try {
      setBillingActionLoading(true);
      await undoWriteOffApi(numericId);
      toast.success("Write-off reversed — item returned to unbilled WIP.");
      await refetchBillingData();
    } catch (error) {
      console.error("Failed to undo write-off:", error);
      toast.error("Failed to undo write-off. Please try again.");
    } finally {
      setBillingActionLoading(false);
    }
  }

  /**
   * Called when the user confirms a credit note in the dialog.
   * Posts to the backend API which creates the credit note in Financials,
   * updates invoice status, and handles WIP disposition.
   */
  async function handleCreditNoteConfirm(result: CreditNoteResult) {
    try {
      setBillingActionLoading(true);
      const creditNote = await createCreditNote(result.invoiceId, {
        creditType: result.creditType,
        creditAmount: result.creditType === "partial" ? result.creditAmount : undefined,
        tamDisposition: result.tamDisposition,
        writeOffReasonCode: result.writeOffReason,
      });

      setCreditNoteTargetInvoice(null);

      const cnNumber = creditNote.creditNoteNumber ?? `CN-${creditNote.id}`;
      const typeLabel = result.creditType === "full" ? "Full credit note" : "Partial credit note";
      const dispositionLabel =
        result.tamDisposition === "return-to-wip"
          ? "Time entries returned to WIP."
          : result.tamDisposition === "write-off"
          ? `Written off (${result.writeOffReasonLabel ?? result.writeOffReason}).`
          : "";
      toast.success(`${typeLabel} raised — ${cnNumber}`, {
        description: [
          `${formatCurrency(result.creditAmount)} credited against ${result.invoiceNumber ?? `Invoice #${result.invoiceId}`}.`,
          dispositionLabel,
        ]
          .filter(Boolean)
          .join(" "),
      });

      await refetchBillingData();
    } catch (error) {
      console.error("Failed to create credit note:", error);
      const serverMessage =
        error && typeof error === "object" && "message" in error
          ? String((error as { message?: unknown }).message ?? "").trim()
          : "";
      toast.error("Failed to create credit note", {
        description: serverMessage || "Please try again.",
      });
      throw error;
    } finally {
      setBillingActionLoading(false);
    }
  }

  return (
    <>
      {/* ── Feature 2: Full-screen Invoice PDF Viewer ─────────────────────────
          Rendered at the root of BillingTab so it sits above the page chrome.
          TODO (Engineering): Once the backend preview API is ready, pass real
          previewData as a prop instead of letting the overlay build mock data. */}
      {/* Send Invoice Dialog (Draft invoice → ... → Send) */}
      <SendInvoiceStep
        asDialog
        open={!!sendDialogInvoice}
        onOpenChange={(open) => { if (!open) setSendDialogInvoice(null); }}
        invoiceNumber={sendDialogInvoice?.invoiceNumber ?? ''}
        invoiceId={sendDialogInvoice?.invoiceId}
        clientEmail={sendDialogInvoice?.clientEmail}
        contactName={client?.contactName}
        contactFirstName={client?.contactFirstName ?? ''}
        clientName={client?.name}
        clientPhone={getClientPhone(client)}
        clientAccountNumber={client?.code}
        clientPaymentTermsDays={client?.dueDateDays != null ? String(client.dueDateDays) : undefined}
        projectName={project?.name}
        projectCode={project?.code}
        companyName={companyDetails.name}
        senderName={companyDetails.name}
        senderEmail={companyDetails.email}
        defaultEmailTemplateId={projectSettings.invoiceEmailTemplateId}
        initialEmailDraft={sendDialogInvoice?.initialEmailDraft}
        onSaveAsDraft={async (emailData) => {
          const draftId = sendDialogInvoice?.invoiceId;
          setSendDialogInvoice(null);
          if (draftId == null) return;
          try {
            await updateDraftEmailData(draftId, {
              to: emailData.to?.length ? emailData.to.join(', ') : undefined,
              cc: emailData.cc?.length ? emailData.cc.join(', ') : undefined,
              bcc: emailData.bcc?.length ? emailData.bcc.join(', ') : undefined,
              subject: emailData.subject || undefined,
              body: emailData.body || undefined,
              templateId: emailData.templateId || undefined,
            });
            await refetchBillingData();
          } catch (error) {
            console.error('Failed to save email draft:', error);
            toast.error('Failed to save email draft. Please try again.');
          }
        }}
        onSent={() => setSendDialogInvoice(null)}
        onEmailerClosed={() => setSendDialogInvoice(null)}
      />

      {viewerInvoice && (
        <InvoiceViewerOverlay
          invoice={viewerInvoice}
          projectName={project?.name ?? "Project"}
          clientName={client?.name ?? "Client"}
          clientEmail={client?.contactEmail}
          clientPhone={getClientPhone(client)}
          clientAccountNumber={client?.code}
          clientPaymentTermsDays={client?.dueDateDays != null ? String(client.dueDateDays) : undefined}
          projectCode={project?.code}
          contactFirstName={client?.contactFirstName ?? ''}
          senderEmail={companyDetails.email}
          templateId={projectSettings.invoiceTemplateId}
          onAuditEntry={addInvoiceSendAuditEvent}
          onClose={() => setViewerInvoice(null)}
        />
      )}

      {/* ── Feature 4: Write-Off Dialog ─────────────────────────────────────── */}
      <WriteOffDialog
        open={writeOffDialogItems.length > 0}
        items={writeOffDialogItems}
        formatCurrency={formatCurrency}
        onClose={() => setWriteOffDialogItems([])}
        onConfirm={handleWriteOffConfirm}
      />

      {/* ── WIP Filter Sheet ────────────────────────────────────────────── */}
      <WipFilterSheet
        open={wipFilterSheetOpen}
        onOpenChange={setWipFilterSheetOpen}
        filters={wipFilters}
        onApply={handleApplyWipFilters}
        availableResources={
          wipFilterOptions.resources.length > 0
            ? wipFilterOptions.resources
            : Array.from(new Set<string>(wipItems.map((item: { resource: string }) => item.resource))).sort()
        }
        writtenOffCount={0}
      />

      {creditNoteTargetInvoice && (
        <CreditNoteDialog
          open={true}
          invoice={creditNoteTargetInvoice}
          projectType={project?.type === "fixed-price" ? "fixed-price" : "time-and-materials"}
          formatCurrency={formatCurrency}
          onClose={() => setCreditNoteTargetInvoice(null)}
          onConfirm={handleCreditNoteConfirm}
        />
      )}

      {/* ── Feature 7: Resend Invoice Dialog ─────────────────────────────────
          Opens when the user clicks "Resend" from the ... menu on a Sent,
          Overdue, or PartiallyPaid invoice row.
          TODO (Engineering): Wire onResent to POST /api/invoices/{id}/resend
          and pass the real clientEmail from the project/client API response. */}
      {resendTargetInvoice && (
        <ResendInvoiceDialog
          open={true}
          onOpenChange={(open) => { if (!open) setResendTargetInvoice(null); }}
          invoiceNumber={resendTargetInvoice.invoiceNumber ?? "Invoice"}
          invoiceId={resendTargetInvoice.id}
          clientEmail={client?.contactEmail ?? ''}
          contactFirstName={client?.contactFirstName ?? ''}
          clientName={client?.name ?? "Client"}
          clientPhone={getClientPhone(client) ?? ''}
          clientAccountNumber={client?.code ?? ''}
          clientPaymentTermsDays={client?.dueDateDays != null ? String(client.dueDateDays) : ''}
          projectName={project.name}
          projectCode={project?.code ?? ''}
          companyName={companyDetails.name}
          senderName={companyDetails.name}
          senderEmail={companyDetails.email}
          invoiceTotal={formatCurrency(resendTargetInvoice.amount)}
          dueDate={resendTargetInvoice.due ?? ""}
          onResent={(entry) => {
            // Push a new send audit event to the project Audit tab.
            // TODO (Engineering): Remove this once the server emits audit events
            // and the Audit tab fetches them from GET /api/projects/:id/audit.
            addInvoiceSendAuditEvent({
              id: entry.id,
              category: "invoice",
              changedAt: entry.sentAt,
              changedBy: entry.sentByName,
              description: `Invoice ${resendTargetInvoice?.invoiceNumber ?? "Invoice"} resent to ${entry.toEmail.join(", ")}${entry.ccEmail?.length ? ` (CC: ${entry.ccEmail.join(", ")})` : ""} — Subject: "${entry.subject}"`,
              metadata: {
                invoiceId: resendTargetInvoice?.id,
                invoiceNumber: resendTargetInvoice?.invoiceNumber,
                toEmail: entry.toEmail,
                ccEmail: entry.ccEmail,
                bccEmail: entry.bccEmail,
                subject: entry.subject,
                isResend: true,
              },
            });
          }}
        />
      )}

                {project?.type === "fixed-price" ? (
                  (() => {
                    // Build BillingMilestone[] — prefer the Sales Budget milestones from
                    // enrichedBudgetData because they carry triggerPercent values (needed for
                    // % complete milestone status derivation in Phase 9). Fall back to the raw
                    // project milestones state only when no Sales Budget data is available.
                    const salesBudgetMilestones = enrichedBudgetData?.salesBudget?.milestones;
                    const billingMilestones: import("@/types/budget.types").BillingMilestone[] =
                      salesBudgetMilestones && salesBudgetMilestones.length > 0
                        ? salesBudgetMilestones
                        : milestones.map((m: Milestone) => ({
                            id: m.id,
                            name: m.name,
                            amount: m.paymentAmount ?? 0,
                            dueDate: m.dueDate,
                            status: (
                              m.status === "completed"
                                ? "paid"
                                : m.status === "overdue"
                                ? "overdue"
                                : new Date(m.dueDate) < new Date()
                                ? "due"
                                : "pending"
                            ) as import("@/types/budget.types").BillingMilestoneStatus,
                          }));

                    // Use the shared fixedFeeInvoices computed at component level (Phase 16).
                    // This ensures the Sales Budget Revenue Health panel and the Billing Tab
                    // both reference the same invoice data without diverging.

                    const contractValue =
                      enrichedBudgetData?.salesBudget?.contractValue ??
                      project.budgetRevenue ??
                      0;

                    return (
                      <FixedFeeBillingTab
                        contractValue={contractValue}
                        milestones={billingMilestones}
                        invoices={fixedFeeInvoices}
                        projectName={project.name}
                        projectCode={project?.code}
                        clientName={client?.name}
                        clientEmail={client?.contactEmail}
                        clientPhone={getClientPhone(client)}
                        clientAccountNumber={client?.code}
                        clientPaymentTermsDays={client?.dueDateDays != null ? String(client.dueDateDays) : undefined}
                        contactFirstName={client?.contactFirstName ?? ''}
                        senderEmail={companyDetails.email}
                        completionPercent={effectiveCompletionPercent}
                        billingTriggerType={project.billingTriggerType}
                        formatCurrency={formatCurrency}
                        onSettingsClick={() => setBillingSettingsDrawerOpen(true)}
                        costCentreConfigured={!!projectSettings.defaultCostCentreCode}
                        initialStatusFilter={invoiceStatusFilter}
                        pdfExistsSet={pdfExistsSet}
                        templateId={projectSettings.invoiceTemplateId}
                        onInvoiceSendAuditEntry={addInvoiceSendAuditEvent}
                        onCreditNoteCreated={refetchBillingData}
                        onGenerateInvoice={(milestone) => {
                          setFixedFeeMilestone({
                            id: milestone.id,
                            name: milestone.name,
                            amount: milestone.amount,
                            dueDate: milestone.dueDate,
                          });
                          setInvoiceWizardOpen(true);
                        }}
                      />
                    );
                  })()
                ) : project?.type !== "time-and-materials" ? (
                  <Card>
                    <CardContent className="flex flex-col items-center justify-center py-12">
                      <div className="rounded-full bg-muted p-3 mb-4">
                        <Receipt className="h-8 w-8 text-muted-foreground" />
                      </div>
                      <h3 className="text-lg font-semibold mb-2">Coming Soon</h3>
                      <p className="text-sm text-muted-foreground text-center max-w-md">
                        Billing features for {formatProjectType(project?.type || "")} projects are currently under
                        development.
                        <br />
                        Time & Materials project billing is available now.
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {/* Billing Sub-tabs */}
                    <Tabs value={billingSubTab} onValueChange={setBillingSubTab} className="w-full">
                      <TabsList className="grid w-full max-w-md grid-cols-2">
                        <TabsTrigger value="invoices">Invoices</TabsTrigger>
                        <TabsTrigger value="unbilled">Unbilled Work</TabsTrigger>
                      </TabsList>

                      {/* Invoices Tab Content */}
                      <TabsContent value="invoices" className="space-y-4 mt-4">
                        {/* Billing Summary - One Line */}
                        {(() => {
                          const budgetRevenue = enrichedBudgetData?.summary?.revenue?.budget ?? project.budgetRevenue;
                          const readyToBill = wipSummary.totalNetAmount;
                          // Budget and Ready to Bill are net of VAT, so Invoiced must be net
                          // too — otherwise the percentage is overstated by the VAT rate.
                          const invoicedNet = invoicedNetTotal;
                          const billingPercent = budgetRevenue > 0 ? (invoicedNet / budgetRevenue) * 100 : 0;
                          const completionPercent = project.completionPercent || 0;
                          const billingGap = billingPercent - completionPercent;

                          return (
                        <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg border border-neutral-200">
                          <div className="flex items-center gap-8 flex-1 flex-wrap">
                            <div>
                              <span className="text-sm text-neutral-600">Budget:</span>
                              <span className="ml-2 text-base font-semibold text-neutral-900">
                                <SensitiveData>{formatCurrency(budgetRevenue)}</SensitiveData>
                              </span>
                            </div>
                            <div>
                              <span className="text-sm text-neutral-600">Invoiced:</span>
                              <span className="ml-2 text-base font-semibold text-neutral-900">
                                <SensitiveData>{formatCurrency(invoicedNet)}</SensitiveData>
                              </span>
                            </div>
                            <div>
                              <span className="text-sm text-neutral-600">Ready to Bill:</span>
                              <span className={`ml-2 text-base font-semibold ${readyToBill < 0 ? "text-error-600" : "text-success-600"}`}>
                                <SensitiveData>{formatCurrency(readyToBill)}</SensitiveData>
                              </span>
                            </div>
                            <div>
                              <span className="text-sm text-neutral-600">Status:</span>
                              <span className="ml-2">
                                {billingLoading ? (
                                  <Badge variant="outline">Loading...</Badge>
                                ) : billingGap < -10 ? (
                                  <Badge variant="destructive">Under-billing</Badge>
                                ) : billingGap < -5 ? (
                                  <Badge variant="warning">Review</Badge>
                                ) : (
                                  <Badge variant="success">On track</Badge>
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                          );
                        })()}

                        {/* Invoice History Table */}
                        <Card>
                          <CardHeader>
                            <div className="flex items-center justify-between">
                              <div>
                                <CardTitle>Invoice History</CardTitle>
                                <CardDescription>All invoices raised for this project</CardDescription>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent className="p-0">
                            {/* Filters and Search */}
                            <div className="flex items-center gap-4 px-6 pt-6 pb-4">
                              <div className="relative flex-1 max-w-sm">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                  placeholder="Search invoices..."
                                  value={invoiceSearchQuery}
                                  onChange={(e) => {
                                    setInvoiceSearchQuery(e.target.value);
                                    setInvoiceCurrentPage(1); // Reset to first page when searching
                                  }}
                                  className="pl-9 pr-9"
                                />
                                {invoiceSearchQuery && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0"
                                    onClick={() => {
                                      setInvoiceSearchQuery("");
                                      setInvoiceCurrentPage(1);
                                    }}
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                              
                              <Select
                                value={invoiceStatusFilter}
                                onValueChange={(value) => {
                                  setInvoiceStatusFilter(value);
                                  setInvoiceCurrentPage(1); // Reset to first page when filtering
                                }}
                              >
                                <SelectTrigger id="status-filter" className="w-[180px]">
                                  <SelectValue placeholder="All Statuses" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="all">All Statuses</SelectItem>
                                  <SelectItem value="Draft">Draft</SelectItem>
                                  <SelectItem value="Sent">Sent</SelectItem>
                                  <SelectItem value="Paid">Paid</SelectItem>
                                  <SelectItem value="Overdue">Overdue</SelectItem>
                                </SelectContent>
                              </Select>

                              {/* ── Card action buttons ── */}
                              <div className="flex items-center gap-2 ml-auto">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      title="Refresh"
                                      onClick={() => refetchBillingData()}
                                      disabled={billingLoading}
                                    >
                                      <RefreshCw className={`h-4 w-4 ${billingLoading ? 'animate-spin' : ''}`} />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Refresh invoice history</p>
                                  </TooltipContent>
                                </Tooltip>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  title="Billing Settings"
                                  onClick={() => setBillingSettingsDrawerOpen(true)}
                                >
                                  <SlidersHorizontal className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  title="Configure Columns"
                                  onClick={() => setInvoiceColumnConfigOpen(true)}
                                >
                                  <Settings2 className="h-4 w-4" />
                                </Button>
                              {/* ── Feature 11: Export to Excel ── */}
                              <Button
                                variant="outline"
                                size="icon"
                                title="Export to Excel"
                                onClick={async () => {
                                  const demoInvoices = [
                                    { id: 1, invoiceNumber: "INV-2024-001", date: "2024-01-15", description: "Monthly Development Services - January", amount: 12500, due: "2024-02-15", status: "Paid" },
                                    { id: 2, invoiceNumber: "INV-2024-002", date: "2024-02-15", description: "Monthly Development Services - February", amount: 15000, due: "2024-03-15", status: "Paid" },
                                    { id: 3, invoiceNumber: "INV-2024-003", date: "2024-03-15", description: "Monthly Development Services - March", amount: 13750, due: "2024-04-15", status: "Overdue" },
                                    { id: 4, invoiceNumber: "INV-2024-004", date: "2024-04-15", description: "Monthly Development Services - April", amount: 14200, due: "2024-05-15", status: "Sent" },
                                    { id: 5, invoiceNumber: "DRAFT-005", date: null, description: "Monthly Development Services - May", amount: 8500, due: null, status: "Draft" },
                                    { id: 6, invoiceNumber: null, date: null, description: "Additional Features Development", amount: 5200, due: null, status: "Draft" },
                                  ].map((inv) => ({ ...inv, ...splitGrossIntoNetAndVat(inv.amount) }));
                                  let source: { invoiceNumber: string | null; date: string | null; description: string; net: number; vat: number; amount: number; due: string | null; status: string }[];
                                  if (dataMode === "live") {
                                    // Fetch ALL invoices (not just current page) for export
                                    const allInvoices = await fetchBillingInvoices(project.id);
                                    source = allInvoices.map((inv: BillingInvoice) => ({
                                      invoiceNumber: inv.invoiceNumber || null,
                                      date: inv.invoiceDate ? inv.invoiceDate.split("T")[0] : null,
                                      description: inv.description || "",
                                      net: inv.subtotal,
                                      vat: inv.taxAmount,
                                      amount: inv.grossTotal,
                                      due: inv.dueDate ? inv.dueDate.split("T")[0] : null,
                                      status: deriveInvoiceDisplayStatus(inv),
                                    }));
                                  } else {
                                    source = demoInvoices;
                                  }
                                  const filtered = source.filter((inv) => {
                                    const matchesStatus = invoiceStatusFilter === "all" || inv.status === invoiceStatusFilter;
                                    const q = invoiceSearchQuery.toLowerCase();
                                    const matchesSearch = q === "" || (inv.invoiceNumber?.toLowerCase().includes(q)) || inv.description?.toLowerCase().includes(q);
                                    return matchesStatus && matchesSearch;
                                  });
                                  // Build rows using only visible columns in configured order
                                  const columnValueExtractors: Record<string, (inv: typeof source[number]) => string | number> = {
                                    'invoice-number': (inv) => inv.invoiceNumber ?? "",
                                    'date': (inv) => inv.date ?? "",
                                    'description': (inv) => inv.description ?? "",
                                    'net': (inv) => inv.net ?? 0,
                                    'vat': (inv) => inv.vat ?? 0,
                                    'amount': (inv) => inv.amount ?? 0,
                                    'days-overdue': (inv) => {
                                      if (!inv.due || inv.status !== 'Overdue') return "";
                                      return Math.max(0, Math.floor((Date.now() - new Date(inv.due).getTime()) / 86400000));
                                    },
                                    'status': (inv) => inv.status ?? "",
                                  };
                                  const rows = filtered.map((inv) => {
                                    const row: Record<string, string | number> = {};
                                    for (const col of visibleInvoiceColumns) {
                                      const extractor = columnValueExtractors[col.id];
                                      if (extractor) {
                                        row[col.name] = extractor(inv);
                                      }
                                    }
                                    return row;
                                  });
                                  const worksheet = XLSX.utils.json_to_sheet(rows);
                                  worksheet["!cols"] = visibleInvoiceColumns
                                    .filter(col => columnValueExtractors[col.id])
                                    .map((col) => ({ wch: col.id === 'description' ? 40 : col.id === 'amount' ? 16 : 14 }));
                                  const workbook = XLSX.utils.book_new();
                                  XLSX.utils.book_append_sheet(workbook, worksheet, "Invoice History");
                                  const safeName = (project?.name ?? "Project").replace(/[^a-z0-9]/gi, "-");
                                  const today = new Date().toISOString().slice(0, 10);
                                  XLSX.writeFile(workbook, `Invoice-History-${safeName}-${today}.xlsx`);
                                }}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                              </div>
                            </div>

                            {(() => {
                              // Invoice data - uses API data in live mode, mock data in demo mode
                              const allInvoices = (dataMode === "live")
                                ? (billingInvoices as BillingInvoice[]).map((inv: BillingInvoice) => ({
                                    id: inv.id,
                                    invoiceNumber: inv.invoiceNumber || null,
                                    date: inv.invoiceDate ? inv.invoiceDate.split("T")[0] : null,
                                    description: inv.description || "",
                                    net: inv.subtotal,
                                    vat: inv.taxAmount,
                                    amount: inv.grossTotal,
                                    created: inv.createdBy ? inv.invoiceDate?.split("T")[0] || null : null,
                                    due: inv.dueDate ? inv.dueDate.split("T")[0] : null,
                                    status: deriveInvoiceDisplayStatus(inv) as "Draft" | "Sent" | "Paid" | "Overdue" | "PartiallyPaid" | "Void" | "WriteOff",
                                    draftEmailTo: inv.draftEmailTo,
                                    draftEmailCc: inv.draftEmailCc,
                                    draftEmailBcc: inv.draftEmailBcc,
                                    draftEmailSubject: inv.draftEmailSubject,
                                    draftEmailBody: inv.draftEmailBody,
                                    draftEmailTemplateId: inv.draftEmailTemplateId,
                                  }))
                                : DEMO_INVOICES;

                              // In live mode, filtering and pagination are handled server-side.
                              // In demo mode, apply client-side filtering as a fallback.
                              const invoices = dataMode === "live"
                                ? allInvoices
                                : allInvoices.filter((invoice: typeof allInvoices[number]) => {
                                    const matchesStatus = invoiceStatusFilter === "all" || invoice.status === invoiceStatusFilter;
                                    const searchLower = invoiceSearchQuery.toLowerCase();
                                    const matchesSearch =
                                      invoiceSearchQuery === "" ||
                                      (invoice.invoiceNumber?.toLowerCase().includes(searchLower)) ||
                                      invoice.description.toLowerCase().includes(searchLower);
                                    return matchesStatus && matchesSearch;
                                  });

                              const getStatusBadge = (status: string) => {
                                switch (status) {
                                  case "Paid":
                                    return <Badge variant="success">Paid</Badge>;
                                  case "Sent":
                                    return <Badge variant="default">Sent</Badge>;
                                  case "Overdue":
                                    return <Badge variant="destructive">Overdue</Badge>;
                                  case "Draft":
                                    return <Badge variant="secondary">Draft</Badge>;
                                  case "PartiallyPaid":
                                    return <Badge variant="warning">Partially Paid</Badge>;
                                  case "Void":
                                    return <Badge variant="outline">Void</Badge>;
                                  // ── Feature 3: Credit Note statuses ──────────────────────────────
                                  case "Credited":
                                    return <Badge variant="secondary" accent="purple">Credited</Badge>;
                                  case "PartiallyCredited":
                                    return <Badge variant="secondary" accent="purple">Partially Credited</Badge>;
                                  case "CreditNote":
                                    return <Badge variant="outline" accent="purple">Credit Note</Badge>;
                                  default:
                                    return <Badge variant="outline">{status}</Badge>;
                                }
                              };

                              // Calculate totals — in live mode use server-provided totals (all invoices);
                              // in demo mode compute from the local dataset. The demo "Invoiced"
                              // figure excludes credited / credit-note rows so it matches the
                              // credit-note netting the server applies in usp_Invoice_GetByProjectIdPaged.
                              const totalInvoiced = dataMode === "live"
                                ? invoiceTotalInvoiced
                                : computeInvoicedTotal(invoices);
                              const outstanding = dataMode === "live"
                                ? invoiceTotalOutstanding
                                : invoices
                                    .filter((inv: typeof invoices[number]) => ["Sent", "Overdue"].includes(inv.status))
                                    .reduce((sum: number, inv: typeof invoices[number]) => sum + inv.amount, 0);
                              const paid = dataMode === "live"
                                ? invoiceTotalPaid
                                : invoices
                                    .filter((inv: typeof invoices[number]) => inv.status === "Paid")
                                    .reduce((sum: number, inv: typeof invoices[number]) => sum + inv.amount, 0);

                              // Invoice table footer total — server totals cover every page in
                              // live mode; in demo mode sum the local dataset.
                              const footerTotal = dataMode === "live"
                                ? invoiceTotalInvoiced
                                : invoices.reduce((sum: number, inv: typeof invoices[number]) => sum + inv.amount, 0);

                              // Build TanStack ColumnDef array from visibleInvoiceColumns
                              const invoiceColumnDefs: ColumnDef<typeof invoices[number], unknown>[] = [
                                ...(visibleInvoiceColumns as ColumnDefinition[]).map((column: ColumnDefinition) => ({
                                  id: column.id,
                                  header: column.name,
                                  accessorFn: (row: typeof invoices[number]) => {
                                    switch (column.id) {
                                      case 'invoice-number': return row.invoiceNumber || `#${row.id}`;
                                      case 'date': return row.date;
                                      case 'description': return row.description;
                                      case 'net': return row.net;
                                      case 'vat': return row.vat;
                                      case 'amount': return row.amount;
                                      case 'days-overdue': {
                                        if (row.status !== 'Overdue' || !row.due) return -1;
                                        return Math.floor((Date.now() - new Date(row.due).getTime()) / 86400000);
                                      }
                                      case 'status': return row.status;
                                      default: return null;
                                    }
                                  },
                                  cell: ({ row }: { row: { original: typeof invoices[number] } }) => {
                                    const invoice = row.original;
                                    switch (column.id) {
                                      case 'invoice-number':
                                        return <span className="font-medium">{invoice.invoiceNumber || `#${invoice.id}`}</span>;
                                      case 'date':
                                        return invoice.date
                                          ? new Date(invoice.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
                                          : "-";
                                      case 'description':
                                        return invoice.description;
                                      case 'net':
                                        return <SensitiveData>{formatCurrency(invoice.net)}</SensitiveData>;
                                      case 'vat':
                                        return <SensitiveData>{formatCurrency(invoice.vat)}</SensitiveData>;
                                      case 'amount':
                                        return (
                                          <span className="font-medium">
                                            <SensitiveData>{formatCurrency(invoice.amount)}</SensitiveData>
                                          </span>
                                        );
                                      case 'days-overdue': {
                                        if (invoice.status !== 'Overdue' || !invoice.due) return <span className="text-muted-foreground">—</span>;
                                        const days = Math.floor((Date.now() - new Date(invoice.due).getTime()) / 86400000);
                                        const bucket = days > 90 ? '90+ days' : days > 60 ? '61–90 days' : days > 30 ? '31–60 days' : '1–30 days';
                                        const colour = days > 60 ? 'text-error-600' : days > 30 ? 'text-warning-700' : 'text-warning-600';
                                        return <span className={`font-medium ${colour}`} title={`${days} days overdue`}>{bucket}</span>;
                                      }
                                      case 'status':
                                        return getStatusBadge(invoice.status);
                                      default:
                                        return null;
                                    }
                                  },
                                  ...(column.align === 'right' ? { meta: { align: 'right' } } : {}),
                                })),
                                {
                                  id: 'actions',
                                  enableSorting: false,
                                  header: () => <span className="sr-only">Actions</span>,
                                  cell: ({ row }: { row: { original: typeof invoices[number] } }) => {
                                    const invoice = row.original;

                                    // ------------------------------------------------------------------
                                    // TODO (Engineering): Wire placeholder actions to real API calls.
                                    // Each "comingSoon" handler below is a temporary stub. Replace with
                                    // actual API mutation calls when the backend endpoints are ready.
                                    // ------------------------------------------------------------------
                                    const comingSoon = (label: string) => {
                                      toast.info(`${label} — coming soon`, {
                                        description: "This action is pending backend integration.",
                                      });
                                    };

                                    // Contextual menu items vary by invoice status (PRD Feature 1).
                                    // Draft    : Edit | Preview | Send | ─ | Delete
                                    // Sent     : View PDF | Resend | Raise Credit Note | Download PDF
                                    // Overdue  : View PDF | Resend | Raise Credit Note | Download PDF
                                    // PartiallyPaid: View PDF | Raise Credit Note | Download PDF
                                    // Paid     : View PDF | Download PDF | Raise Credit Note
                                    // Credited : View PDF | Download PDF
                                    // Void     : View PDF (read-only)
                                    // Credit Note : View PDF | Download PDF

                                    const isDraft = invoice.status === "Draft";

                                    // Draft → Edit: open the Invoice Generation Wizard
                                    // pre-populated with the saved draft's lines & values so the
                                    // user can edit / remove lines, return to WIP, or continue to
                                    // preview & send.
                                    const handleEditDraft = () => {
                                      handleEditDraftInvoice(invoice.id);
                                    };

                                    const handleViewPdf = async () => {
                                      const stored = await getStoredInvoicePdf(String(invoice.id));
                                      if (stored) {
                                        openPdfInNewTab(stored.pdfBlob);
                                      } else {
                                        setViewerInvoice({
                                          id: invoice.id,
                                          invoiceNumber: invoice.invoiceNumber,
                                          date: invoice.date,
                                          due: invoice.due,
                                          description: invoice.description,
                                          amount: invoice.amount,
                                          status: invoice.status as InvoiceViewerInvoice["status"],
                                        });
                                      }
                                    };

                                    return (
                                      <div className="flex items-center justify-end">
                                        {/* Delete confirmation dialog — only needed for Draft; rendered
                                            outside the DropdownMenu so AlertDialog can control its own
                                            open state independently of the dropdown. */}
                                        {isDraft ? (
                                          <AlertDialog>
                                            {/* The dropdown trigger lives inside an AlertDialog so we can
                                                keep the delete confirm dialog co-located. We use a custom
                                                open-state approach: the DropdownMenuItem calls
                                                AlertDialogTrigger programmatically via a hidden trigger ref. */}
                                            <DropdownMenu>
                                              <DropdownMenuTrigger asChild>
                                                <Button
                                                  variant="ghost"
                                                  size="sm"
                                                  className="h-8 w-8 p-0"
                                                  aria-label="Invoice actions"
                                                >
                                                  <MoreHorizontal className="h-4 w-4" />
                                                </Button>
                                              </DropdownMenuTrigger>
                                              <DropdownMenuContent align="end" className="w-44">
                                                {/* "Edit" opens the Invoice Generation Wizard
                                                    pre-populated with this draft's saved lines & values. */}
                                                <DropdownMenuItem onClick={handleEditDraft}>
                                                  <Eye className="h-4 w-4" />
                                                  Edit
                                                </DropdownMenuItem>

                                                {/* TODO (Engineering): "Preview" should render the
                                                    invoice PDF preview in a modal/sheet. */}
                                                <DropdownMenuItem onClick={() => comingSoon("Preview")}>
                                                  <FileText className="h-4 w-4" />
                                                  Preview
                                                </DropdownMenuItem>

                                                {/* TODO (Engineering): "Send" triggers the
                                                    send-invoice flow (email template selection, send). */}
                                                <DropdownMenuItem onClick={() => {
                                                  const inv = invoice as typeof invoice & { draftEmailTo?: string; draftEmailCc?: string; draftEmailBcc?: string; draftEmailSubject?: string; draftEmailBody?: string; draftEmailTemplateId?: string };
                                                  setSendDialogInvoice({
                                                    invoiceNumber: invoice.invoiceNumber ?? `DRAFT-${invoice.id}`,
                                                    invoiceId: invoice.id,
                                                    clientEmail: client?.contactEmail,
                                                    initialEmailDraft: (inv.draftEmailTo || inv.draftEmailCc || inv.draftEmailBcc || inv.draftEmailSubject || inv.draftEmailBody || inv.draftEmailTemplateId)
                                                      ? { to: inv.draftEmailTo, cc: inv.draftEmailCc, bcc: inv.draftEmailBcc, subject: inv.draftEmailSubject, body: inv.draftEmailBody, templateId: inv.draftEmailTemplateId }
                                                      : undefined,
                                                  });
                                                }}>
                                                  <Send className="h-4 w-4" />
                                                  Send
                                                </DropdownMenuItem>

                                                <DropdownMenuSeparator />

                                                {/* Delete uses AlertDialog for confirmation. We render
                                                    AlertDialogTrigger as the menu item so Radix handles
                                                    the modal state correctly. */}
                                                <AlertDialogTrigger asChild>
                                                  <DropdownMenuItem
                                                    variant="destructive"
                                                    disabled={billingActionLoading}
                                                    onSelect={(e) => e.preventDefault()}
                                                  >
                                                    <Trash2 className="h-4 w-4" />
                                                    Delete
                                                  </DropdownMenuItem>
                                                </AlertDialogTrigger>
                                              </DropdownMenuContent>
                                            </DropdownMenu>

                                            <AlertDialogContent>
                                              <AlertDialogHeader>
                                                <AlertDialogTitle>Delete Draft Invoice</AlertDialogTitle>
                                                <AlertDialogDescription>
                                                  This will delete the draft invoice and return all associated WIP items back to Unbilled Work. The line items on this draft will be removed.
                                                </AlertDialogDescription>
                                              </AlertDialogHeader>
                                              <AlertDialogFooter>
                                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                <AlertDialogAction onClick={() => handleDeleteDraft(invoice.id)}>
                                                  Delete &amp; Return to WIP
                                                </AlertDialogAction>
                                              </AlertDialogFooter>
                                            </AlertDialogContent>
                                          </AlertDialog>
                                        ) : (
                                          <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-8 w-8 p-0"
                                                aria-label="Invoice actions"
                                              >
                                                <MoreHorizontal className="h-4 w-4" />
                                              </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end" className="w-52">

                                              {/* ---- Actions available for non-Draft invoices ---- */}

                                              {/* "View PDF" — shown for all non-Draft statuses.
                                                  Opens the full-screen InvoiceViewerOverlay (Feature 2).
                                                  TODO (Engineering): Once the overlay fetches real preview
                                                  data from the backend, remove this TODO comment. */}
                                              {["Sent", "Overdue", "PartiallyPaid", "Paid", "Credited", "Void", "CreditNote"].includes(invoice.status) && (
                                                <DropdownMenuItem onClick={handleViewPdf}>
                                                  <Eye className="h-4 w-4" />
                                                  View PDF
                                                </DropdownMenuItem>
                                              )}

                                              {/* "Resend" — Sent, Overdue, and PartiallyPaid.
                                                  Opens ResendInvoiceDialog (Feature 7).
                                                  TODO (Engineering): onResent in the dialog should call
                                                  POST /api/invoices/{id}/resend and record the send event. */}
                                              {["Sent", "Overdue", "PartiallyPaid"].includes(invoice.status) && (
                                                <DropdownMenuItem
                                                  onClick={() =>
                                                    setResendTargetInvoice({
                                                      id: invoice.id,
                                                      invoiceNumber: invoice.invoiceNumber,
                                                      amount: invoice.amount,
                                                      due: invoice.due,
                                                    })
                                                  }
                                                >
                                                  <RefreshCw className="h-4 w-4" />
                                                  Resend
                                                </DropdownMenuItem>
                                              )}

                                              {/* "Raise Credit Note" — Sent, Overdue, PartiallyPaid, Paid. */}
                                              {["Sent", "Overdue", "PartiallyPaid", "Paid"].includes(invoice.status) && (
                                                <DropdownMenuItem
                                                  onClick={() =>
                                                    setCreditNoteTargetInvoice({
                                                      id: invoice.id,
                                                      invoiceNumber: invoice.invoiceNumber,
                                                      amount: invoice.amount,
                                                      status: invoice.status,
                                                    })
                                                  }
                                                >
                                                  <CreditCard className="h-4 w-4" />
                                                  Raise Credit Note
                                                </DropdownMenuItem>
                                              )}

                                              {["Sent", "Overdue", "PartiallyPaid", "Paid", "Credited", "CreditNote"].includes(invoice.status) && (
                                                <DropdownMenuItem onClick={async () => {
                                                  const stored = await getStoredInvoicePdf(String(invoice.id));
                                                  if (stored) {
                                                    downloadPdf(stored.pdfBlob, `${invoice.invoiceNumber ?? `Invoice-${invoice.id}`}.pdf`);
                                                  } else {
                                                    toast.info("No PDF available", { description: "A PDF has not been generated for this invoice yet." });
                                                  }
                                                }}>
                                                  <Download className="h-4 w-4" />
                                                  Download PDF
                                                </DropdownMenuItem>
                                              )}

                                            </DropdownMenuContent>
                                          </DropdownMenu>
                                        )}
                                      </div>
                                    );
                                  },
                                },
                              ];

                              // Build column visibility state
                              const invoiceColumnVisibility = columnDefinitionsToVisibility(invoiceColumns);

                              // Apply client-side multi-column sorting within the current page
                              const sortedInvoices = [...invoices].sort((a, b) => {
                                for (const sort of invoiceColumnSorts) {
                                  let aVal: string | number | null = null;
                                  let bVal: string | number | null = null;
                                  switch (sort.columnId) {
                                    case 'invoice-number': aVal = a.invoiceNumber || ''; bVal = b.invoiceNumber || ''; break;
                                    case 'date': aVal = a.date || ''; bVal = b.date || ''; break;
                                    case 'description': aVal = a.description; bVal = b.description; break;
                                    case 'net': aVal = a.net; bVal = b.net; break;
                                    case 'vat': aVal = a.vat; bVal = b.vat; break;
                                    case 'amount': aVal = a.amount; bVal = b.amount; break;
                                    case 'days-overdue': {
                                      aVal = (a.status === 'Overdue' && a.due) ? Math.floor((Date.now() - new Date(a.due).getTime()) / 86400000) : -1;
                                      bVal = (b.status === 'Overdue' && b.due) ? Math.floor((Date.now() - new Date(b.due).getTime()) / 86400000) : -1;
                                      break;
                                    }
                                    case 'status': aVal = a.status; bVal = b.status; break;
                                  }
                                  if (aVal === null || bVal === null) continue;
                                  const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
                                  if (cmp !== 0) return sort.direction === 'asc' ? cmp : -cmp;
                                }
                                return 0;
                              });

                              // In live mode pagination is server-side; in demo mode paginate client-side
                              const serverTotal = dataMode === "live" ? invoiceTotalCount : sortedInvoices.length;
                              const invoicePagination = calculatePagination(serverTotal, invoiceCurrentPage, invoiceItemsPerPage);
                              const displayInvoices = dataMode === "live"
                                ? sortedInvoices
                                : sortedInvoices.slice(invoicePagination.startIndex, invoicePagination.endIndex);

                              return (
                                <>
                                  <DataTableEnhanced<typeof sortedInvoices[number], unknown>
                                    className="rounded-none border-0 border-t [&_tbody_tr:hover]:odd:bg-neutral-50 [&_tbody_tr:hover]:even:bg-white"
                                    columns={invoiceColumnDefs}
                                    data={displayInvoices}
                                    loading={billingLoading}
                                    columnVisibility={invoiceColumnVisibility}
                                    columnSorts={invoiceColumnSorts}
                                    onColumnSortChange={(newSorts) => {
                                      setInvoiceColumnSorts(newSorts);
                                      setInvoiceCurrentPage(1);
                                    }}
                                    getRowId={(row) => String(row.id)}
                                    emptyMessage={
                                      <div className="space-y-2">
                                        <Receipt className="h-12 w-12 mx-auto opacity-50" />
                                        <p className="text-lg font-medium">No invoice data available</p>
                                        <p className="text-sm">Invoice history will appear here once integrated with your billing system</p>
                                      </div>
                                    }
                                    footerContent={
                                      serverTotal > 0 ? (
                                        <tr className="bg-muted/50 font-semibold border-t-2">
                                          {(visibleInvoiceColumns as ColumnDefinition[]).map((col: ColumnDefinition, index: number) => {
                                            if (index === 0) return (
                                              <td key={col.id} className="px-4 py-2 text-xs text-muted-foreground whitespace-nowrap">
                                                Total ({serverTotal} {serverTotal === 1 ? 'invoice' : 'invoices'})
                                              </td>
                                            );
                                            if (col.id === 'amount') return (
                                              <td key={col.id} className="px-4 py-2 text-right text-xs font-semibold">
                                                <SensitiveData>{formatCurrency(footerTotal)}</SensitiveData>
                                              </td>
                                            );
                                            return <td key={col.id} className="px-4 py-2" />;
                                          })}
                                          {/* Actions column */}
                                          <td className="px-4 py-2" />
                                        </tr>
                                      ) : null
                                    }
                                    paginationContent={
                                      serverTotal > 0 ? (
                                        <PaginationControls
                                          pagination={invoicePagination}
                                          onPageChange={(page) => setInvoiceCurrentPage(page)}
                                          onPageSizeChange={(size) => { setInvoiceItemsPerPage(size); setInvoiceCurrentPage(1); }}
                                          showPageSizeSelector={true}
                                          className="w-full"
                                        />
                                      ) : null
                                    }
                                  />

                                  {/* Footer with totals */}
                                  {SHOW_INVOICE_SUMMARY_TOTALS && serverTotal > 0 && (
                                    <div className="border-t px-10 py-4 flex justify-between">
                                        <div className="space-y-1">
                                          <div className="text-sm text-muted-foreground">Total Invoiced</div>
                                          <div className="text-xl font-bold">
                                            <SensitiveData>{formatCurrency(totalInvoiced)}</SensitiveData>
                                          </div>
                                        </div>
                                        <div className="space-y-1">
                                          <div className="text-sm text-muted-foreground">Outstanding</div>
                                          <div className="text-xl font-bold text-warning-600">
                                            <SensitiveData>{formatCurrency(outstanding)}</SensitiveData>
                                          </div>
                                        </div>
                                        <div className="space-y-1">
                                          <div className="text-sm text-muted-foreground">Paid</div>
                                          <div className="text-xl font-bold text-success-600">
                                            <SensitiveData>{formatCurrency(paid)}</SensitiveData>
                                          </div>
                                        </div>
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </CardContent>
                        </Card>

                      </TabsContent>

                      {/* Unbilled Work Tab Content */}
                      <TabsContent value="unbilled" className="space-y-4 mt-4">
                        {/* WIP Summary - One Line (uses server-side summary for accurate totals across all pages) */}
                        {(() => {
                          const adjustedTotal = wipSummary.totalNetAmount;
                          const adjustedTime = wipSummary.timeNetAmount;
                          const adjustedExpense = wipSummary.expenseNetAmount;

                          return (
                        <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg border border-neutral-200">
                          <div className="flex items-center gap-8 flex-1">
                            <div>
                              <span className="text-sm text-neutral-600">Time WIP:</span>
                              <span className="ml-2 text-base font-semibold text-neutral-900">
                                <SensitiveData>{formatCurrency(adjustedTime)}</SensitiveData>
                              </span>
                              <span className="ml-1 text-xs text-neutral-500">({wipSummary.timeHours.toFixed(1)} hrs)</span>
                            </div>
                            <div>
                              <span className="text-sm text-neutral-600">Expense WIP:</span>
                              <span className="ml-2 text-base font-semibold text-neutral-900">
                                <SensitiveData>{formatCurrency(adjustedExpense)}</SensitiveData>
                              </span>
                            </div>
                            <div>
                              <span className="text-sm text-neutral-600">Total WIP:</span>
                              <span className="ml-2 text-base font-semibold text-success-600">
                                <SensitiveData>{formatCurrency(adjustedTotal)}</SensitiveData>
                              </span>
                            </div>
                            <div>
                              <span className="text-sm text-neutral-600">Status:</span>
                              <span className="ml-2">
                                {billingLoading ? (
                                  <Badge variant="outline">Loading...</Badge>
                                ) : adjustedTotal > 0 ? (
                                  <Badge variant="success">Ready to Bill</Badge>
                                ) : (
                                  <Badge variant="secondary">No Items</Badge>
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                          );
                        })()}

                        {/* Aged WIP Strip */}
                        <AgedWipStrip
                          wipItems={filteredAndSortedWipItems as WipItemForAging[]}
                          writtenOffIds={new Set<string>()}
                          activeBand={activeAgeBand}
                          onBandClick={setActiveAgeBand}
                        />

                        {/* WIP Items Table */}
                        <Card>
                          <CardHeader>
                            <div className="flex items-center justify-between">
                              <div>
                                <CardTitle>Unbilled Work Items</CardTitle>
                                <CardDescription>Select items to include in the next invoice</CardDescription>
                              </div>
                              <div className="flex items-center gap-2">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={handleRefreshWip}
                                      disabled={billingLoading}
                                    >
                                      <RefreshCw className={`h-4 w-4 ${billingLoading ? 'animate-spin' : ''}`} />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Refresh unbilled work items</p>
                                  </TooltipContent>
                                </Tooltip>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setWipColumnConfigOpen(true)}
                                >
                                  <Settings2 className="h-4 w-4 mr-2" />
                                  Configure
                                </Button>
                                {dataMode === "live" && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="gap-2"
                                    disabled={selectedWipItems.length === 0 || billingActionLoading}
                                    onClick={() => setCreateDraftDialogOpen(true)}
                                  >
                                    <FileText className="h-4 w-4" />
                                    Create Draft
                                  </Button>
                                )}
                                {/* ── Feature 4: Show written-off toggle ───────────────────────
                                    Only visible once ≥1 item has been written off.
                                    Clicking toggles visibility of written-off rows in the table.
                                ────────────────────────────────────────────────────────── */}
                                {/* ── Feature 4b: Filters button ───────────────────────────── */}
                                {(() => {
                                  const activeCount = countActiveWipFilters(wipFilters);
                                  return (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="relative"
                                      onClick={() => setWipFilterSheetOpen(true)}
                                    >
                                      <Filter className="h-4 w-4" />
                                      {activeCount > 0 && (
                                        <span className="absolute -top-1.5 -right-1.5 text-[10px] font-semibold text-primary-foreground min-w-[18px] h-[18px] rounded-full bg-primary inline-flex items-center justify-center">
                                          {activeCount}
                                        </span>
                                      )}
                                    </Button>
                                  );
                                })()}
                                {/* ── Feature 4: Write Off button ──────────────────────────────
                                    Enabled when ≥1 non-written-off WIP item is selected.
                                    TODO (Engineering): replace setWriteOffDialogItems with a real
                                    API call: POST /api/billing/wip/write-off
                                    Body: { itemIds: string[], reason?: string }
                                ────────────────────────────────────────────────────────── */}
                                {(() => {
                                  const selectedNonWrittenOff = selectedWipItems;
                                  const hasSelection = selectedNonWrittenOff.length > 0;
                                  return (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="gap-2 disabled:opacity-40"
                                      disabled={!hasSelection || billingActionLoading}
                                      onClick={() => {
                                        const items: WriteOffItem[] = filteredAndSortedWipItems
                                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                          .filter((i: any) => selectedNonWrittenOff.includes(i.id))
                                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                          .map((i: any) => ({
                                            id: String(i.id),
                                            description: i.description,
                                            resource: i.resource,
                                            type: i.type as "Time" | "Expense",
                                            amount: i.amount,
                                            quantity: i.type === "Time" ? i.quantity : undefined,
                                          }));
                                        setWriteOffDialogItems(items);
                                      }}
                                    >
                                      <Ban className="h-4 w-4" />
                                      Write Off{hasSelection && selectedNonWrittenOff.length > 1
                                        ? ` (${selectedNonWrittenOff.length})`
                                        : ""}
                                    </Button>
                                  );
                                })()}
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span tabIndex={!client ? 0 : undefined}>
                                      <Button 
                                        className="gap-2" 
                                        disabled={!client || selectedWipItems.length === 0 || billingActionLoading}
                                        onClick={() => setInvoiceWizardOpen(true)}
                                        style={!client ? { pointerEvents: "none" } : undefined}
                                      >
                                        <FileText className="h-4 w-4" />
                                        Generate Invoice
                                      </Button>
                                    </span>
                                  </TooltipTrigger>
                                  {!client && (
                                    <TooltipContent>
                                      <p>Project has no Client set, Please set Client before you can Generate Invoice</p>
                                    </TooltipContent>
                                  )}
                                </Tooltip>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent className="p-0">
                            {/* Search bar — stays inline for instant filtering */}
                            <div className="flex items-center gap-2 px-6 pt-6 pb-4">
                              <div className="relative flex-1 max-w-sm">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                                <Input
                                  placeholder="Search by description..."
                                  className="pl-9"
                                  value={wipSearchQuery}
                                  onChange={(e) => setWipSearchQuery(e.target.value)}
                                />
                              </div>
                              {wipSearchQuery && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setWipSearchQuery("")}
                                  className="text-muted-foreground"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              )}
                            </div>

                            {/* WIP Table — DataTableEnhanced */}
                            {(() => {
                              type WipRow = typeof filteredAndSortedWipItems[number];

                              const baseWipItems = filteredAndSortedWipItems;

                              // Apply age band filter if one is active
                              const ageBandFilter = activeAgeBand
                                ? (item: typeof filteredAndSortedWipItems[number]) => {
                                    const today = new Date();
                                    const itemDate = new Date(item.date);
                                    const days = Math.floor((today.getTime() - itemDate.getTime()) / (1000 * 60 * 60 * 24));
                                    if (activeAgeBand === '0-30') return days <= 30;
                                    if (activeAgeBand === '31-60') return days >= 31 && days <= 60;
                                    if (activeAgeBand === '61-90') return days >= 61 && days <= 90;
                                    if (activeAgeBand === '91-120') return days >= 91 && days <= 120;
                                    if (activeAgeBand === '121+') return days >= 121;
                                    return true;
                                  }
                                : null;

                              const displayedWipItems = ageBandFilter
                                ? baseWipItems.filter(ageBandFilter)
                                : baseWipItems;

                              const allDisplayedTotal = displayedWipItems.reduce((sum: number, item: WipRow) => sum + item.amount, 0);
                              const allDisplayedQuantity = sumWipQuantity(displayedWipItems);

                              const wipColumnDefs: ColumnDef<WipRow, unknown>[] = [
                                // Checkbox select column
                                {
                                  id: 'select',
                                  enableSorting: false,
                                  header: () => {
                                    const selectableIds = displayedWipItems
                                      .map((item) => item.id);
                                    const allSelectableSelected =
                                      selectableIds.length > 0 &&
                                      selectableIds.every((id) => selectedWipItems.includes(id));
                                    return (
                                      <input
                                        type="checkbox"
                                        className="rounded"
                                        checked={allSelectableSelected}
                                        onChange={() => {
                                          // Select / deselect only non-written-off items
                                          if (allSelectableSelected) {
                                            setSelectedWipItems([]);
                                          } else {
                                            setSelectedWipItems(selectableIds);
                                          }
                                        }}
                                      />
                                    );
                                  },
                                  cell: ({ row }: { row: { original: WipRow } }) => (
                                    <input
                                      type="checkbox"
                                      className="rounded"
                                      checked={selectedWipItems.includes(row.original.id)}
                                      onChange={() => handleSelectWipItem(row.original.id)}
                                      disabled={false}
                                    />
                                  ),
                                },
                                // Data columns from visible WIP column preferences
                                ...(visibleWipColumns as ColumnDefinition[]).map((column: ColumnDefinition) => ({
                                  id: column.id,
                                  header: column.name,
                                  accessorFn: (row: WipRow) => {
                                    switch (column.id) {
                                      case 'date': return row.date;
                                      case 'resource': return row.resource;
                                      case 'task': return row.task;
                                      case 'description': return row.description;
                                      case 'type': return row.type;
                                      case 'quantity': return row.quantity;
                                      case 'rate': return row.rate;
                                      case 'amount': return row.amount;
                                      case 'costCentre': return row.costCentre;
                                      default: return null;
                                    }
                                  },
                                  cell: ({ row }: { row: { original: WipRow } }) => {
                                    const item = row.original;
                                    const align = column.align === 'right' ? 'text-right' : '';
                                    switch (column.id) {
                                      case 'date':
                                        return <span>{item.date}</span>;
                                      case 'resource':
                                        return <span>{item.resource}</span>;
                                      case 'task':
                                        return <span>{item.task}</span>;
                                      case 'description':
                                        return (
                                          <span className="flex items-center gap-2">
                                            <span>
                                              {item.description}
                                            </span>
                                          </span>
                                        );
                                      case 'type':
                                        return (
                                          <Badge variant={item.type === 'Time' ? 'outline' : 'secondary'}>
                                            {item.type}
                                          </Badge>
                                        );
                                      case 'quantity':
                                        return <span className={align}>{item.quantity.toFixed(2)}</span>;
                                      case 'rate':
                                        return (
                                          <span className={align}>
                                            <SensitiveData>{formatCurrency(item.rate)}</SensitiveData>
                                          </span>
                                        );
                                      case 'amount':
                                        return (
                                          <span className={align}>
                                            <SensitiveData>{formatCurrency(item.amount)}</SensitiveData>
                                          </span>
                                        );
                                      case 'costCentre':
                                        return <span>{item.costCentre}</span>;
                                      default:
                                        return <span>-</span>;
                                    }
                                  },
                                  ...(column.align === 'right' ? { meta: { align: 'right' } } : {}),
                                })),

                              ];

                              const wipColumnVisibility = columnDefinitionsToVisibility(wipColumns);

                              // Decide which rows to render: grouped rows or paginated flat rows
                              const wipPagination = calculatePagination(wipTotalItems, wipCurrentPage, wipItemsPerPage);

                              // For grouped view build a flat list with group-header pseudo-rows inserted
                              const tableData: (WipRow & { _isGroupHeader?: boolean; _groupKey?: string; _groupItems?: WipRow[] })[] =
                                wipGroupBy !== 'none' && groupedWipItems
                                  ? Object.entries(groupedWipItems as Record<string, WipRow[]>).flatMap(([groupKey, groupItems]: [string, WipRow[]]) => [
                                      // Pseudo-row for the group header (rendered via custom row)
                                      { id: `__group__${groupKey}`, _isGroupHeader: true, _groupKey: groupKey, _groupItems: groupItems } as any,
                                      ...groupItems,
                                    ])
                                  : paginatedWipItems;

                              return (
                                <DataTableEnhanced<WipRow, unknown>
                                  className="rounded-none border-0 border-t [&_tbody_tr:hover]:odd:bg-neutral-50 [&_tbody_tr:hover]:even:bg-white"
                                  columns={wipColumnDefs}
                                  data={tableData}
                                  loading={billingLoading}
                                  columnVisibility={{ ...wipColumnVisibility, select: true }}
                                  columnSorts={wipColumnSorts}
                                  onColumnSortChange={(newSorts) => {
                                    setWipColumnSorts(newSorts);
                                    setWipCurrentPage(1);
                                  }}
                                  getRowId={(row) => String(row.id)}
                                  customRowRenderer={(row) => {
                                    const r = row.original as any;
                                    if (!r._isGroupHeader) return null; // use default rendering

                                    const groupItems: WipRow[] = r._groupItems ?? [];
                                    const groupTotal = groupItems.reduce((sum, item) => sum + item.amount, 0);
                                    const colCount = visibleWipColumns.length + 1; // +1 for checkbox col

                                    return (
                                      <tr key={`group-${r._groupKey}`} className="bg-muted/30 border-b">
                                        <td
                                          colSpan={(visibleWipColumns as ColumnDefinition[]).findIndex((c: ColumnDefinition) => c.id === 'quantity') + 1}
                                          className="px-4 py-2 font-semibold text-sm"
                                        >
                                          {r._groupKey} ({groupItems.length} items)
                                        </td>
                                        {(visibleWipColumns as ColumnDefinition[]).find((c: ColumnDefinition) => c.id === 'quantity') && (
                                          <td className="px-4 py-2" />
                                        )}
                                        {(visibleWipColumns as ColumnDefinition[]).find((c: ColumnDefinition) => c.id === 'rate') && (
                                          <td className="px-4 py-2" />
                                        )}
                                        {(visibleWipColumns as ColumnDefinition[]).find((c: ColumnDefinition) => c.id === 'amount') && (
                                          <td className="px-4 py-2 text-right font-semibold text-sm">
                                            <SensitiveData>{formatCurrency(groupTotal)}</SensitiveData>
                                          </td>
                                        )}
                                        {(visibleWipColumns as ColumnDefinition[]).find((c: ColumnDefinition) => c.id === 'costCentre') && (
                                          <td className="px-4 py-2" />
                                        )}
                                        {/* fill remaining cols to colCount */}
                                        {Array.from({ length: Math.max(0, colCount - visibleWipColumns.length - 1) }).map((_, i) => (
                                          <td key={i} />
                                        ))}
                                      </tr>
                                    );
                                  }}
                                  emptyMessage={
                                    <div className="space-y-2">
                                      <FileText className="h-12 w-12 mx-auto opacity-50" />
                                      <p className="text-lg font-medium">No unbilled work items</p>
                                      <p className="text-sm">Time and expense entries will appear here once recorded</p>
                                    </div>
                                  }
                                  footerContent={
                                    filteredAndSortedWipItems.length > 0 ? (
                                      <tr className="bg-muted/50 font-semibold border-t-2 relative">
                                        {/* Checkbox column */}
                                        <td className="px-4 py-2" />
                                        {(() => {
                                          const hasSelection = selectedWipTotals.count > 0;
                                          const footerLabel = hasSelection ? 'Selected Total' : 'Total';
                                          const footerCount = hasSelection ? selectedWipTotals.count : displayedWipItems.length;
                                          const footerQuantity = hasSelection ? selectedWipTotals.quantity : allDisplayedQuantity;
                                          const footerAmount = hasSelection ? selectedWipTotals.amount : allDisplayedTotal;
                                          return (visibleWipColumns as ColumnDefinition[]).map((col: ColumnDefinition) => {
                                            if (col.id === 'date') return (
                                              <td key="date" className="px-4 py-2 relative">
                                                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xs text-muted-foreground whitespace-nowrap pointer-events-none">
                                                  {footerLabel} ({footerCount} items)
                                                </span>
                                              </td>
                                            );
                                            if (col.id === 'resource' || col.id === 'task' || col.id === 'description' || col.id === 'type') return (
                                              <td key={col.id} className="px-4 py-2" />
                                            );
                                            if (col.id === 'quantity') return (
                                              <td key="quantity" className="px-4 py-2 text-right text-xs">
                                                {footerQuantity > 0 ? footerQuantity.toFixed(2) : '-'}
                                              </td>
                                            );
                                            if (col.id === 'rate') return (
                                              <td key="rate" className="px-4 py-2" />
                                            );
                                            if (col.id === 'amount') return (
                                              <td key="amount" className="px-4 py-2 text-right text-xs font-semibold">
                                                <SensitiveData>{formatCurrency(footerAmount)}</SensitiveData>
                                              </td>
                                            );
                                            if (col.id === 'costCentre') return <td key="costCentre" className="px-4 py-2" />;
                                            return <td key={col.id} className="px-4 py-2" />;
                                          });
                                        })()}
                                      </tr>
                                    ) : null
                                  }
                                  paginationContent={
                                    wipGroupBy === 'none' && wipTotalItems > 0 ? (
                                      <PaginationControls
                                        pagination={wipPagination}
                                        onPageChange={(page) => handleWipPageChange(page)}
                                        onPageSizeChange={(size) => handleWipPageSizeChange(size)}
                                        showPageSizeSelector={true}
                                        className="w-full"
                                      />
                                    ) : null
                                  }
                                />
                              );
                            })()}
                          </CardContent>
                        </Card>
                      </TabsContent>
                    </Tabs>
                  </div>
                )}

    </>
  );
}
